import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { PATHS } from './paths';
import AuthGuard from 'src/layouts/guard/auth_guard';
import DashboardLayout from 'src/layouts/dashboard/dashboard_layout';
import RoleGuard, { ROLES } from 'src/layouts/guard/role_guard';
import SettingsGuard from 'src/layouts/guard/settings_guard';
import SettingsIndexRedirect from 'src/layouts/guard/settings_index_redirect';

const DashboardPage = lazy(() => import('src/pages/dashboard/dashboard_page'));
const OrderPage = lazy(() => import('src/pages/dashboard/order/order_page'));
const OrderAddPage = lazy(() => import('src/pages/dashboard/order/order_add_page'));
const OrderEditPage = lazy(() => import('src/pages/dashboard/order/order_edit_page'));
const B2BOrderPage = lazy(() => import('src/pages/dashboard/bulk_order/b2b/b2b_order_page'));
const B2BOrderAddPage = lazy(() => import('src/pages/dashboard/bulk_order/b2b/b2b_order_add_page'));
const B2BOrderEditPage = lazy(
  () => import('src/pages/dashboard/bulk_order/b2b/b2b_order_edit_page'),
);
const WithdrawalPage = lazy(
  () => import('src/pages/dashboard/bulk_order/withdrawal/withdrawal_page'),
);
const WithdrawalAddPage = lazy(
  () => import('src/pages/dashboard/bulk_order/withdrawal/withdrawal_add_page'),
);
const WithdrawalEditPage = lazy(
  () => import('src/pages/dashboard/bulk_order/withdrawal/withdrawal_edit_page'),
);
const TransferPage = lazy(() => import('src/pages/dashboard/bulk_order/transfer/transfer_page'));
const TransferAddPage = lazy(
  () => import('src/pages/dashboard/bulk_order/transfer/transfer_add_page'),
);
const TransferEditPage = lazy(
  () => import('src/pages/dashboard/bulk_order/transfer/transfer_edit_page'),
);
const ReturnOrderPage = lazy(() => import('src/pages/dashboard/return_order/return_order_page'));
const ReturnOrderEditPage = lazy(
  () => import('src/pages/dashboard/return_order/return_order_edit_page'),
);
const ReturnOrderAddPage = lazy(
  () => import('src/pages/dashboard/return_order/return_order_add_page'),
);
const PurchaseOrderPage = lazy(
  () => import('src/pages/dashboard/inventory/purchase_order/purchase_order_page'),
);
const PurchaseOrderAddPage = lazy(
  () => import('src/pages/dashboard/inventory/purchase_order/purchase_order_add_page'),
);
const PurchaseOrderEditPage = lazy(
  () => import('src/pages/dashboard/inventory/purchase_order/purchase_order_edit_page'),
);
const SettingsUsersPage = lazy(() => import('src/pages/dashboard/settings/users/users/users_page'));
const SettingsUsersAddPage = lazy(
  () => import('src/pages/dashboard/settings/users/users/users_add_page'),
);
const SettingsUsersEditPage = lazy(
  () => import('src/pages/dashboard/settings/users/users/users_edit_page'),
);
const SettingsUsersLocationPage = lazy(
  () => import('src/pages/dashboard/settings/users/users/users_location_page'),
);
const SettingsUsersCustomerPage = lazy(
  () => import('src/pages/dashboard/settings/users/users/users_customer_page'),
);
const SettingsLocationsPage = lazy(
  () => import('src/pages/dashboard/settings/locations/locations/locations_page'),
);
const SettingsLocationsAddPage = lazy(
  () => import('src/pages/dashboard/settings/locations/locations/locations_add_page'),
);
const SettingsLocationsEditPage = lazy(
  () => import('src/pages/dashboard/settings/locations/locations/locations_edit_page'),
);
const SettingsLocationManagementPage = lazy(
  () =>
    import('src/pages/dashboard/settings/locations/location_management/location_management_page'),
);
const SettingsLocationManagementAddPage = lazy(
  () =>
    import(
      'src/pages/dashboard/settings/locations/location_management/location_management_add_page'
    ),
);
const SettingsLocationManagementEditPage = lazy(
  () =>
    import(
      'src/pages/dashboard/settings/locations/location_management/location_management_edit_page'
    ),
);
const SettingsPackingMaterialsPage = lazy(
  () => import('src/pages/dashboard/settings/locations/packing_materials/packing_materials_page'),
);
const SettingsPackingMaterialsAddPage = lazy(
  () =>
    import('src/pages/dashboard/settings/locations/packing_materials/packing_materials_add_page'),
);
const SettingsPackingMaterialsEditPage = lazy(
  () =>
    import('src/pages/dashboard/settings/locations/packing_materials/packing_materials_edit_page'),
);
const SettingsSorterManagementPage = lazy(
  () => import('src/pages/dashboard/settings/locations/sorter_management/sorter_management_page'),
);
const SettingsSorterManagementAddPage = lazy(
  () =>
    import('src/pages/dashboard/settings/locations/sorter_management/sorter_management_add_page'),
);
const SettingsSorterManagementEditPage = lazy(
  () =>
    import('src/pages/dashboard/settings/locations/sorter_management/sorter_management_edit_page'),
);
const SettingsMachinePage = lazy(
  () => import('src/pages/dashboard/settings/locations/machine/machine_page'),
);
const SettingsMachineAddPage = lazy(
  () => import('src/pages/dashboard/settings/locations/machine/machine_add_page'),
);
const SettingsMachineEditPage = lazy(
  () => import('src/pages/dashboard/settings/locations/machine/machine_edit_page'),
);
const SettingsManifestSettingPage = lazy(
  () => import('src/pages/dashboard/settings/locations/manifest_setting/manifest_setting_page'),
);
const SettingsManifestSettingAddPage = lazy(
  () => import('src/pages/dashboard/settings/locations/manifest_setting/manifest_setting_add_page'),
);
const SettingsManifestSettingEditPage = lazy(
  () =>
    import('src/pages/dashboard/settings/locations/manifest_setting/manifest_setting_edit_page'),
);
const SettingsShipperSettingPage = lazy(
  () => import('src/pages/dashboard/settings/users/shipper_setting/shipper_setting_page'),
);
const SettingsShipperSettingAddPage = lazy(
  () => import('src/pages/dashboard/settings/users/shipper_setting/shipper_setting_add_page'),
);
const SettingsShipperSettingEditPage = lazy(
  () => import('src/pages/dashboard/settings/users/shipper_setting/shipper_setting_edit_page'),
);
const SettingsChannelsPage = lazy(
  () => import('src/pages/dashboard/settings/channels/channels_page'),
);
const SettingsChannelsAddPage = lazy(
  () => import('src/pages/dashboard/settings/channels/channels_add_page'),
);
const SettingsChannelsEditPage = lazy(
  () => import('src/pages/dashboard/settings/channels/channels_edit_page'),
);
const SettingsMappingCodePage = lazy(
  () => import('src/pages/dashboard/settings/mapping_code/mapping_code_page'),
);
const SettingsInvoiceMaskingPage = lazy(
  () => import('src/pages/dashboard/settings/invoice_masking/invoice_masking_page'),
);
const SettingsWebhooksPage = lazy(
  () => import('src/pages/dashboard/settings/api/webhooks/webhooks_page'),
);
const SettingsAppReleasePage = lazy(
  () => import('src/pages/dashboard/settings/app_release/app_release_page'),
);
const SettingsFeatureSettingPage = lazy(
  () => import('src/pages/dashboard/settings/feature_setting/feature_setting_page'),
);
const SettingsFeatureSettingAddPage = lazy(
  () => import('src/pages/dashboard/settings/feature_setting/feature_setting_add_page'),
);
const SettingsFeatureSettingEditPage = lazy(
  () => import('src/pages/dashboard/settings/feature_setting/feature_setting_edit_page'),
);
const SupplierPage = lazy(() => import('src/pages/dashboard/settings/supplier/supplier_page'));
const SupplierAddPage = lazy(
  () => import('src/pages/dashboard/settings/supplier/supplier_add_page'),
);
const SupplierEditPage = lazy(
  () => import('src/pages/dashboard/settings/supplier/supplier_edit_page'),
);

const InboundPage = lazy(() => import('src/pages/dashboard/inbound/inbound/inbound_page'));
const InboundDetailPage = lazy(
  () => import('src/pages/dashboard/inbound/inbound/inbound_detail_page'),
);
const InboundDetailHistoryPage = lazy(
  () => import('src/pages/dashboard/inbound/inbound/inbound_detail_history_page'),
);
const PutawayPage = lazy(() => import('src/pages/dashboard/inbound/putaway/putaway_page'));
const PutawayEditPage = lazy(() => import('src/pages/dashboard/inbound/putaway/putaway_edit_page'));
const BulkPutawayPage = lazy(
  () => import('src/pages/dashboard/inbound/bulk_putaway/bulk_putaway_page'),
);
const BulkPutawayEditPage = lazy(
  () => import('src/pages/dashboard/inbound/bulk_putaway/bulk_putaway_edit_page'),
);
const InventoryTransferPage = lazy(
  () => import('src/pages/dashboard/inventory/transfer/transfer_page'),
);
const InventoryTransferEditPage = lazy(
  () => import('src/pages/dashboard/inventory/transfer/transfer_edit_page'),
);
const BulkTransferPage = lazy(
  () => import('src/pages/dashboard/inventory/bulk_transfer/bulk_transfer_page'),
);
const BulkTransferEditPage = lazy(
  () => import('src/pages/dashboard/inventory/bulk_transfer/bulk_transfer_edit_page'),
);
const OutboundPage = lazy(() => import('src/pages/dashboard/outbound/outbound/outbound_page'));
const OutboundEditPage = lazy(
  () => import('src/pages/dashboard/outbound/outbound/outbound_edit_page'),
);

const ShippingScanPage = lazy(
  () => import('src/pages/dashboard/outbound/shipping_scan/shipping_scan_page'),
);
const CartonPage = lazy(() => import('src/pages/dashboard/export/carton/carton_page'));
const LabelPrintingPage = lazy(() => import('src/pages/dashboard/printing/label_printing_page'));
const DocumentPrintingPage = lazy(
  () => import('src/pages/dashboard/printing/document_printing_page'),
);
const CartonEditPage = lazy(() => import('src/pages/dashboard/export/carton/carton_edit_page'));
const ManifestPage = lazy(() => import('src/pages/dashboard/export/manifest/manifest_page'));
const ManifestEditPage = lazy(
  () => import('src/pages/dashboard/export/manifest/manifest_edit_page'),
);
const ReadyToManifestPage = lazy(
  () => import('src/pages/dashboard/export/ready_to_manifest/ready_to_manifest_page'),
);
const AdvanceTrackingNumberPage = lazy(
  () => import('src/pages/dashboard/outbound/advance_tracking_number/advance_tracking_number_page'),
);
const PickingPage = lazy(() => import('src/pages/dashboard/outbound/picking/picking_page'));
const PickingTotalPage = lazy(
  () => import('src/pages/dashboard/outbound/picking/picking_total_page'),
);
const PickingWavePage = lazy(
  () => import('src/pages/dashboard/outbound/picking/picking_wave_page'),
);
const SortingPage = lazy(() => import('src/pages/dashboard/outbound/sorting/sorting_page'));
const SortingScanPage = lazy(
  () => import('src/pages/dashboard/outbound/sorting/sorting_scan_page'),
);
const SortingDetailPage = lazy(
  () => import('src/pages/dashboard/outbound/sorting/sorting_detail_page'),
);
const PackingPage = lazy(() => import('src/pages/dashboard/outbound/packing/packing_page'));
const PackingMultiPage = lazy(
  () => import('src/pages/dashboard/outbound/packing/packing_multi_page'),
);
const SinglePackingPage = lazy(
  () => import('src/pages/dashboard/outbound/packing/single_packing_page'),
);

const BulkOutboundPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_outbound/bulk_outbound_page'),
);
const BulkOutboundEditPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_outbound/bulk_outbound_edit_page'),
);
const BulkPickingPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_picking/bulk_picking_page'),
);
const BulkPickingTotalPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_picking/bulk_picking_total_page'),
);

const BulkPackingPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_packing/bulk_packing_page'),
);
const BulkPackingMultiPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_packing/bulk_packing_detail_page'),
);
const BulkShippingScanPage = lazy(
  () => import('src/pages/dashboard/bulk_outbound/bulk_shipping_scan/bulk_shipping_scan_page'),
);

const ProductPage = lazy(() => import('src/pages/dashboard/inventory/product/product_page'));
const ProductAddPage = lazy(() => import('src/pages/dashboard/inventory/product/product_add_page'));
const ProductEditPage = lazy(
  () => import('src/pages/dashboard/inventory/product/product_edit_page'),
);

const OuterboxPage = lazy(() => import('src/pages/dashboard/inventory/outerbox/outerbox_page'));
const OuterboxAddPage = lazy(
  () => import('src/pages/dashboard/inventory/outerbox/outerbox_add_page'),
);
const OuterboxEditPage = lazy(
  () => import('src/pages/dashboard/inventory/outerbox/outerbox_edit_page'),
);

const StockManagementPage = lazy(
  () => import('src/pages/dashboard/inventory/stock_management/stock_management_page'),
);

const ReturnInboundPage = lazy(
  () => import('src/pages/dashboard/return_inbound/return_inbound/return_inbound_page'),
);
const ReturnInboundScanTrackingPage = lazy(
  () => import('src/pages/dashboard/return_inbound/scan_tracking/scan_tracking_page'),
);
const ReturnInboundDetailPage = lazy(
  () => import('src/pages/dashboard/return_inbound/return_inbound/return_inbound_detail_page'),
);

const AdjustmentTasksPage = lazy(
  () => import('src/pages/dashboard/inventory/adjustment_tasks/adjustment_tasks_page'),
);
const AdjustmentTasksEditPage = lazy(
  () => import('src/pages/dashboard/inventory/adjustment_tasks/adjustment_tasks_edit_page'),
);
const AdjustmentManagementPage = lazy(
  () => import('src/pages/dashboard/inventory/adjustment_management/adjustment_management_page'),
);
const AdjustmentManagementAddPage = lazy(
  () =>
    import('src/pages/dashboard/inventory/adjustment_management/adjustment_management_add_page'),
);
const AdjustmentManagementEditPage = lazy(
  () =>
    import('src/pages/dashboard/inventory/adjustment_management/adjustment_management_edit_page'),
);

const order = {
  path: 'order',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.CUSTOMER, ROLES.STAFF]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'order',
      children: [
        { element: <OrderPage />, index: true },
        {
          path: 'new',
          element: <OrderAddPage />,
        },
        {
          path: 'edit/:id',
          element: <OrderEditPage />,
        },
      ],
    },
  ],
};

const bulkOrder = {
  path: 'bulkorder',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.CUSTOMER, ROLES.STAFF]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'b2b',
      children: [
        { element: <B2BOrderPage />, index: true },
        {
          path: 'new',
          element: <B2BOrderAddPage />,
        },
        {
          path: 'edit/:id',
          element: <B2BOrderEditPage />,
        },
      ],
    },
    {
      path: 'withdrawal',
      children: [
        { element: <WithdrawalPage />, index: true },
        {
          path: 'new',
          element: <WithdrawalAddPage />,
        },
        {
          path: 'edit/:id',
          element: <WithdrawalEditPage />,
        },
      ],
    },
    {
      path: 'transfer',
      children: [
        { element: <TransferPage />, index: true },
        {
          path: 'new',
          element: <TransferAddPage />,
        },
        {
          path: 'edit/:id',
          element: <TransferEditPage />,
        },
      ],
    },
  ],
};

const returnOrder = {
  path: 'return',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.CUSTOMER, ROLES.STAFF]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'order',
      children: [
        { element: <ReturnOrderPage />, index: true },
        {
          path: 'edit/:id',
          element: <ReturnOrderEditPage />,
        },
        {
          path: 'new',
          element: <ReturnOrderAddPage />,
        },
      ],
    },
  ],
};

const returnInbound = {
  path: 'return_inbound',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    { element: <ReturnInboundPage />, index: true },
    {
      path: 'detail/:returnId',
      element: <ReturnInboundDetailPage />,
    },
  ],
};

const returnInboundScanTracking = {
  path: 'scan_tracking',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <ReturnInboundScanTrackingPage />
    </RoleGuard>
  ),
};

const inbound = {
  path: 'inbound',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'inbound',
      children: [
        { element: <InboundPage />, index: true },
        {
          path: 'edit/:id',
          element: <InboundDetailPage />,
        },
        {
          path: 'edit/:purchaseOrderId/:purchaseOrderLine',
          element: <InboundDetailHistoryPage />,
        },
      ],
    },
    {
      path: 'bulk_putaway',
      children: [
        { element: <BulkPutawayPage />, index: true },
        {
          path: 'edit/:mode/:target',
          element: <BulkPutawayEditPage />,
        },
      ],
    },
    {
      path: 'putaway',
      children: [
        { element: <PutawayPage />, index: true },
        {
          path: 'edit/:productCode',
          element: <PutawayEditPage />,
        },
      ],
    },
  ],
};

const outbound = {
  path: 'outbound',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'outbound',
      children: [
        { element: <OutboundPage />, index: true },
        {
          path: 'edit/:id',
          element: <OutboundEditPage />,
        },
      ],
    },
    {
      path: 'shipping',
      children: [{ path: 'scan', element: <ShippingScanPage />, index: true }],
    },
    {
      path: 'advance-tracking-number',
      children: [{ element: <AdvanceTrackingNumberPage />, index: true }],
    },
    {
      path: 'picking',
      children: [
        { element: <PickingPage />, index: true },
        {
          path: 'total/:id',
          element: <PickingTotalPage />,
        },
        {
          path: 'wave/:id',
          element: <PickingWavePage />,
        },
      ],
    },
    {
      path: 'packing',
      children: [
        { element: <PackingPage />, index: true },
        {
          path: 'multi/:locationId/:scanNumber',
          element: <PackingMultiPage />,
        },
      ],
    },
    {
      path: 'single-packing',
      children: [{ element: <SinglePackingPage />, index: true }],
    },
    {
      path: 'sorting',
      children: [
        { element: <SortingPage />, index: true },
        {
          path: 'scan/:id',
          element: <SortingScanPage />,
        },
        {
          path: 'detail/:id',
          element: <SortingDetailPage />,
        },
      ],
    },
  ],
};

const bulkOutbound = {
  path: 'bulk_outbound',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'bulk-outbound',
      children: [
        { element: <BulkOutboundPage />, index: true },
        {
          path: 'edit/:id',
          element: <BulkOutboundEditPage />,
        },
      ],
    },
    {
      path: 'picking',
      children: [
        { element: <BulkPickingPage />, index: true },
        {
          path: 'total/:id',
          element: <BulkPickingTotalPage />,
        },
      ],
    },
    {
      path: 'packing',
      children: [
        { element: <BulkPackingPage />, index: true },
        {
          path: ':locationId/:type/:scanNumber',
          element: <BulkPackingMultiPage />,
        },
      ],
    },
    {
      path: 'shipping',
      children: [{ path: 'scan', element: <BulkShippingScanPage />, index: true }],
    },
  ],
};

const inventory = {
  path: 'inventory',
  element: (
    <RoleGuard
      allowedRoles={[
        ROLES.ADMIN,
        ROLES.MANAGER,
        ROLES.LOCATION_MANAGER,
        ROLES.WORKER,
        ROLES.STAFF,
        ROLES.CUSTOMER,
      ]}
    >
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'bulk_transfer',
      children: [
        { element: <BulkTransferPage />, index: true },
        {
          path: 'edit/:mode/:target',
          element: <BulkTransferEditPage />,
        },
      ],
    },
    {
      path: 'transfer',
      children: [
        { element: <InventoryTransferPage />, index: true },
        {
          path: 'edit/:productCode',
          element: <InventoryTransferEditPage />,
        },
      ],
    },
    {
      path: 'products',
      children: [
        { element: <ProductPage />, index: true },
        {
          path: 'new',
          element: <ProductAddPage />,
        },
        {
          path: 'edit/:userId/:id',
          element: <ProductEditPage />,
        },
      ],
    },
    {
      path: 'outerbox',
      children: [
        { element: <OuterboxPage />, index: true },
        {
          path: 'new',
          element: <OuterboxAddPage />,
        },
        {
          path: 'edit/:id',
          element: <OuterboxEditPage />,
        },
      ],
    },
    {
      path: 'stock',
      children: [
        { element: <StockManagementPage />, index: true },
        { path: 'edit/:userId/:id', element: <ProductEditPage /> },
      ],
    },
    {
      path: 'adjustment-tasks',
      children: [
        { element: <AdjustmentTasksPage />, index: true },
        {
          path: 'edit/:id',
          element: <AdjustmentTasksEditPage />,
        },
      ],
    },
    {
      path: 'adjustment-management',
      children: [
        { element: <AdjustmentManagementPage />, index: true },
        {
          path: 'new',
          element: <AdjustmentManagementAddPage />,
        },
        {
          path: 'edit/:id',
          element: <AdjustmentManagementEditPage />,
        },
      ],
    },
    {
      path: 'purchase-order',
      element: (
        <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.CUSTOMER, ROLES.STAFF]}>
          <Outlet />
        </RoleGuard>
      ),
      children: [
        { element: <PurchaseOrderPage />, index: true },
        {
          path: 'new',
          element: <PurchaseOrderAddPage />,
        },
        {
          path: 'edit/:id',
          element: <PurchaseOrderEditPage />,
        },
      ],
    },
  ],
};

const dashboard = {
  path: 'root',
  element: <DashboardPage />,
};

const SETTINGS_PAGES = [
  {
    path: 'users',
    menuKey: 'users',
    element: <SettingsUsersPage />,
    add: <SettingsUsersAddPage />,
    edit: <SettingsUsersEditPage />,
    routes: [
      { path: 'location/:id', element: <SettingsUsersLocationPage /> },
      { path: 'customer/:id', element: <SettingsUsersCustomerPage /> },
    ],
  },
  {
    path: 'locations',
    menuKey: 'locations',
    element: <SettingsLocationsPage />,
    add: <SettingsLocationsAddPage />,
    edit: <SettingsLocationsEditPage />,
  },
  {
    path: 'location-management',
    menuKey: 'location_management',
    element: <SettingsLocationManagementPage />,
    routes: [
      { path: ':type/new', element: <SettingsLocationManagementAddPage /> },
      {
        path: ':type/edit/:locationId/:number',
        element: <SettingsLocationManagementEditPage />,
      },
    ],
  },
  {
    path: 'packing-materials',
    menuKey: 'packing_materials',
    element: <SettingsPackingMaterialsPage />,
    add: <SettingsPackingMaterialsAddPage />,
    edit: <SettingsPackingMaterialsEditPage />,
  },
  {
    path: 'sorter-management',
    menuKey: 'sorter_management',
    element: <SettingsSorterManagementPage />,
    add: <SettingsSorterManagementAddPage />,
    routes: [
      {
        path: 'edit/:locationId/:id',
        element: <SettingsSorterManagementEditPage />,
      },
    ],
  },
  {
    path: 'machine',
    menuKey: 'machine',
    element: <SettingsMachinePage />,
    add: <SettingsMachineAddPage />,
    edit: <SettingsMachineEditPage />,
  },
  {
    path: 'manifest-setting',
    menuKey: 'manifest_setting',
    element: <SettingsManifestSettingPage />,
    add: <SettingsManifestSettingAddPage />,
    edit: <SettingsManifestSettingEditPage />,
  },
  {
    path: 'shipper-setting',
    menuKey: 'shipper_setting',
    element: <SettingsShipperSettingPage />,
    add: <SettingsShipperSettingAddPage />,
    edit: <SettingsShipperSettingEditPage />,
  },
  {
    path: 'channels',
    menuKey: 'channels',
    element: <SettingsChannelsPage />,
    add: <SettingsChannelsAddPage />,
    edit: <SettingsChannelsEditPage />,
  },
  { path: 'mapping-code', menuKey: 'mapping_code', element: <SettingsMappingCodePage /> },
  {
    path: 'invoice-masking',
    menuKey: ['invoice_masking', 'shipping_item_name'],
    element: <SettingsInvoiceMaskingPage />,
  },
  { path: 'webhooks', menuKey: 'webhooks', element: <SettingsWebhooksPage /> },
  {
    path: 'supplier',
    menuKey: 'suppliers',
    element: <SupplierPage />,
    add: <SupplierAddPage />,
    edit: <SupplierEditPage />,
  },
  {
    path: 'feature-setting',
    menuKey: 'feature_setting',
    element: <SettingsFeatureSettingPage />,
    add: <SettingsFeatureSettingAddPage />,
    edit: <SettingsFeatureSettingEditPage />,
  },
  { path: 'app-release', menuKey: 'app_release', element: <SettingsAppReleasePage /> },
];

const settings = {
  path: 'settings',
  children: [
    ...SETTINGS_PAGES.map(({ path, menuKey, element, add, edit, routes }) => ({
      path: path,
      element: (
        <SettingsGuard menuKey={menuKey}>
          <Outlet />
        </SettingsGuard>
      ),
      children: [
        { element: element, index: true },
        ...(add ? [{ path: 'new', element: add }] : []),
        ...(edit ? [{ path: 'edit/:id', element: edit }] : []),
        ...(routes ?? []),
      ],
    })),
    {
      element: <SettingsIndexRedirect />,
      index: true,
    },
  ],
};

const printing = {
  path: 'printing',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      element: (
        <Navigate
          to={PATHS.dashboard.printing.label}
          replace
        />
      ),
      index: true,
    },
    { path: 'label', element: <LabelPrintingPage /> },
    { path: 'document', element: <DocumentPrintingPage /> },
  ],
};

const exportCarton = {
  path: 'export',
  element: (
    <RoleGuard allowedRoles={[ROLES.ADMIN, ROLES.MANAGER, ROLES.LOCATION_MANAGER, ROLES.WORKER]}>
      <Outlet />
    </RoleGuard>
  ),
  children: [
    {
      path: 'carton',
      children: [
        { element: <CartonPage />, index: true },
        { path: ':id', element: <CartonEditPage /> },
      ],
    },
    {
      path: 'manifest',
      children: [
        { element: <ManifestPage />, index: true },
        { path: ':id', element: <ManifestEditPage /> },
      ],
    },
    { path: 'ready-to-manifest', element: <ReadyToManifestPage /> },
  ],
};

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      dashboard,
      order,
      bulkOrder,
      returnOrder,
      inbound,
      outbound,
      exportCarton,
      printing,
      inventory,
      returnInbound,
      returnInboundScanTracking,
      bulkOutbound,
      settings,
      {
        element: (
          <Navigate
            to={PATHS.dashboard.root}
            replace
          />
        ),
        index: true,
      },
    ],
  },
];
